import React, { useState, useEffect } from 'react';
import { api, traitsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Flame, Footprints, Star, Swords } from 'lucide-react';
import { SkillStars, FormMeter, FootBadge, TraitBadge, formBreakdownLabel } from './PlayerTraits';
import { PLAY_STYLES } from '@/lib/traitMeta';

const OUTCOME = {
  win: { letter: 'V', cls: 'bg-green-500', title: 'Vittoria' },
  draw: { letter: 'P', cls: 'bg-yellow-500', title: 'Pareggio' },
  loss: { letter: 'S', cls: 'bg-red-500', title: 'Sconfitta' },
};

const FOOT_TITLE = { right: 'Destro', left: 'Sinistro', both: 'Ambidestro' };
const STYLE_TITLE = Object.fromEntries(PLAY_STYLES.map(({ key, label }) => [key, label]));

/**
 * The player card for one sport: overall (skill), form with where it comes
 * from, foot and weak foot, trait tags, and the player's record.
 *
 * `profile` is the normalized sport profile from the public-profile API
 * (skill_level, form, form_manual, form_breakdown, traits, preferred_foot, weak_foot, play_style).
 */
export const PlayerCard = ({ userId, sportId = 'sport_calcetto', profile, showDetailed = true }) => {
  const [loading, setLoading] = useState(!!userId);
  const [stats, setStats] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [attributes, setAttributes] = useState([]);

  useEffect(() => {
    if (!userId) return;
    api.get(`/stats/player/${userId}`)
      .then((res) => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => {
    traitsAPI.getAll(sportId)
      .then((res) => {
        setCatalog(res.data.traits || []);
        setAttributes(res.data.attributes || []);
      })
      .catch(() => {});
  }, [sportId]);

  const p = profile || {};
  const has = (key) => attributes.some((a) => a.key === key);
  const skill = Number(p.skill_level ?? 3);
  const form = Number(p.form_manual ?? p.form ?? 3);   // raw or normalized profile
  const recent = p.form_breakdown?.recent || [];
  const traits = p.traits || [];

  return (
    <Card data-testid="player-card">
      <CardContent className="p-4 space-y-4">
        {/* Overall */}
        <div className="flex items-center gap-4">
          <div className="text-center">
            <p className="text-4xl font-bold leading-none tabular-nums" data-testid="player-card-overall">{skill.toFixed(1)}</p>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Livello</p>
          </div>
          <div className="flex-1 space-y-1.5">
            <SkillStars level={skill} size="md" />
            <div className="flex items-center gap-2 flex-wrap">
              <FormMeter level={form} breakdown={p.form_breakdown} size="md" />
              <span className="text-xs text-muted-foreground">Forma {form.toFixed(1)}</span>
              {p.form_manual != null && <Badge variant="outline" className="text-[10px]">manuale</Badge>}
            </div>
          </div>
        </div>

        {/* Recent matches feeding the form */}
        <div className="space-y-1.5">
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Flame className="h-3 w-3" /> {formBreakdownLabel(p.form_breakdown)}
          </p>
          {recent.length > 0 && (
            <div className="flex gap-1">
              {recent.map((r, i) => {
                const o = OUTCOME[r.outcome] || { letter: '?', cls: 'bg-gray-400', title: 'N/A' };
                return (
                  <div
                    key={`${r.match_id}-${i}`}
                    className={`relative w-7 h-7 rounded-full ${o.cls} flex items-center justify-center text-white text-xs font-bold`}
                    title={`${o.title}${r.mvp_won ? ' · MVP' : r.mvp_votes ? ` · ${r.mvp_votes} voti MVP` : ''}`}
                  >
                    {o.letter}
                    {r.mvp_won && <Star className="absolute -top-1 -right-1 h-3 w-3 fill-amber-400 text-amber-400" />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Calcetto-only attributes */}
        {(has('preferred_foot') || has('weak_foot')) && (
          <div className="grid grid-cols-2 gap-3 pt-3 border-t">
            {has('preferred_foot') && (
              <div className="flex items-center gap-2">
                <Footprints className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-semibold text-sm">{FOOT_TITLE[p.preferred_foot] || '—'}</p>
                  <p className="text-xs text-muted-foreground">Piede</p>
                </div>
              </div>
            )}
            {has('weak_foot') && (
              <div>
                <SkillStars level={p.weak_foot ?? 0} size="sm" />
                <p className="text-xs text-muted-foreground">Piede debole {p.weak_foot != null ? Number(p.weak_foot).toFixed(1) : '—'}</p>
              </div>
            )}
            {has('play_style') && (
              <div className="flex items-center gap-2">
                <Swords className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="font-semibold text-sm">{STYLE_TITLE[p.play_style] || '—'}</p>
                  <p className="text-xs text-muted-foreground">Attitudine</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Traits */}
        {traits.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {traits.map((k) => <TraitBadge key={k} traitKey={k} catalog={catalog} size="md" />)}
            <FootBadge foot={has('preferred_foot') ? null : p.preferred_foot} size="md" />
          </div>
        )}

        {/* Record */}
        {loading ? <Skeleton className="h-16" /> : stats && showDetailed && (
          <div className="grid grid-cols-3 gap-3 pt-3 border-t">
            <div className="text-center p-2 rounded-lg bg-green-500/10">
              <p className="text-xl font-bold text-green-600">{stats.wins}</p>
              <p className="text-xs text-muted-foreground">Vittorie</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-yellow-500/10">
              <p className="text-xl font-bold text-yellow-600">{stats.draws}</p>
              <p className="text-xs text-muted-foreground">Pareggi</p>
            </div>
            <div className="text-center p-2 rounded-lg bg-red-500/10">
              <p className="text-xl font-bold text-red-600">{stats.losses}</p>
              <p className="text-xs text-muted-foreground">Sconfitte</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const PlayerStatsCard = PlayerCard;
export default PlayerCard;
